/**
 * i18n — English/Spanish UI strings (react-i18next).
 * Procedure terms come from the server procedure catalog; the local
 * procedure catalog layer was removed.
 *
 * Language follows the device/system locale (no in-app switcher):
 * Spanish OS/region or any preferred `es*` tag → 'es', otherwise 'en'.
 *
 * Boot loads a shell bundle first (common/legal/dashboard/onboarding/library)
 * so first paint is not blocked on newSurgery/report/legal-heavy namespaces.
 * The rest fills in immediately after createRoot.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { enUS, es as dateFnsEs } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import { importWithRetry, isChunkLoadError, tryRecoverFromStaleChunk } from '@/lib/lazy-retry';

export type AppLanguage = 'en' | 'es';

/** @deprecated No in-app picker — kept for any leftover imports. */
export const APP_LANGUAGES: { id: AppLanguage; label: string }[] = [
  { id: 'en', label: 'English' },
  { id: 'es', label: 'Español' },
];

function isSpanishTag(tag: string | undefined | null): boolean {
  return typeof tag === 'string' && tag.toLowerCase().startsWith('es');
}

/** Best-effort OS/region locale (often Spanish when Chrome UI language is still en). */
export function readIntlLocale(
  /** Pass `null` to skip Intl (tests). Omit to use the global Intl. */
  intl?: Pick<typeof Intl, 'DateTimeFormat'> | null,
): string | undefined {
  const api =
    intl === undefined
      ? typeof Intl !== 'undefined'
        ? Intl
        : undefined
      : (intl ?? undefined);
  if (!api?.DateTimeFormat) return undefined;
  try {
    return api.DateTimeFormat().resolvedOptions().locale;
  } catch {
    return undefined;
  }
}

export type DetectLanguageInput = {
  /** navigator.language */
  language?: string;
  /** navigator.languages */
  languages?: readonly string[];
  /** Intl.DateTimeFormat().resolvedOptions().locale — system/region */
  intlLocale?: string | null;
};

/**
 * UI language from the device/system locale + browser preferred languages.
 * Order: Intl system/region locale → navigator.language → navigator.languages.
 * Any `es*` tag wins (Chrome often keeps `en-US` as language while the OS
 * region or a secondary preferred language is Spanish). No Prefs.idioma.
 */
export function detectLanguage(
  input?: DetectLanguageInput,
): AppLanguage {
  const resolved: DetectLanguageInput =
    input ??
    (typeof navigator !== 'undefined'
      ? {
          language: navigator.language,
          languages: navigator.languages,
          intlLocale: readIntlLocale(),
        }
      : {});

  const tags = [
    resolved.intlLocale,
    resolved.language,
    ...(resolved.languages ?? []),
  ];
  for (const tag of tags) {
    if (isSpanishTag(tag)) return 'es';
  }
  return 'en';
}

/** Keep <html lang> in sync for a11y / date formatting hints. */
export function applyDocumentLanguage(lang: AppLanguage): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lang === 'es' ? 'es' : 'en';
}

/** date-fns locale matching the active UI language. */
export function activeDateLocale(): Locale {
  return i18n.language === 'es' ? dateFnsEs : enUS;
}

async function importShell(lang: AppLanguage): Promise<Record<string, unknown>> {
  // `landing` is in the shell: it is the first paint for signed-out visitors
  // arriving from a search result. Retry + one-shot reload on stale hashes
  // (Mobile Safari on / — SUTURIA-WEB-8).
  return importWithRetry(async () => {
    if (lang === 'es') {
      const [common, landing, legal, guide, features, dashboard, onboarding, library] =
        await Promise.all([
          import('./locales/es/common'),
          import('./locales/es/landing'),
          import('./locales/es/legal'),
          import('./locales/es/guide'),
          import('./locales/es/features'),
          import('./locales/es/dashboard'),
          import('./locales/es/onboarding'),
          import('./locales/es/library'),
        ]);
      return {
        common: common.default,
        landing: landing.default,
        legal: legal.default,
        guide: guide.default,
        features: features.default,
        dashboard: dashboard.default,
        onboarding: onboarding.default,
        library: library.default,
      };
    }
    const [common, landing, legal, guide, features, dashboard, onboarding, library] =
      await Promise.all([
        import('./locales/en/common'),
        import('./locales/en/landing'),
        import('./locales/en/legal'),
        import('./locales/en/guide'),
        import('./locales/en/features'),
        import('./locales/en/dashboard'),
        import('./locales/en/onboarding'),
        import('./locales/en/library'),
      ]);
    return {
      common: common.default,
      landing: landing.default,
      legal: legal.default,
      guide: guide.default,
      features: features.default,
      dashboard: dashboard.default,
      onboarding: onboarding.default,
      library: library.default,
    };
  });
}

async function importDeferred(lang: AppLanguage): Promise<Record<string, unknown>> {
  // Retry + one-shot reload on stale hashes (Mobile Safari — SUTURIA-WEB-8).
  // Locale key is `procedures` (glossary/techniques pages removed on this branch).
  return importWithRetry(async () => {
    if (lang === 'es') {
      const [
        account,
        community,
        ficha,
        procedures,
        instruments,
        newSurgery,
        report,
        research,
        profile,
        settings,
        surgeryDetail,
      ] = await Promise.all([
        import('./locales/es/account'),
        import('./locales/es/community'),
        import('./locales/es/ficha'),
        import('./locales/es/procedures'),
        import('./locales/es/instruments'),
        import('./locales/es/newSurgery'),
        import('./locales/es/report'),
        import('./locales/es/research'),
        import('./locales/es/profile'),
        import('./locales/es/settings'),
        import('./locales/es/surgeryDetail'),
      ]);
      return {
        account: account.default,
        community: community.default,
        ficha: ficha.default,
        procedures: procedures.default,
        instruments: instruments.default,
        newSurgery: newSurgery.default,
        report: report.default,
        research: research.default,
        profile: profile.default,
        settings: settings.default,
        surgeryDetail: surgeryDetail.default,
      };
    }
    const [
      account,
      community,
      ficha,
      procedures,
      instruments,
      newSurgery,
      report,
      research,
      profile,
      settings,
      surgeryDetail,
    ] = await Promise.all([
      import('./locales/en/account'),
      import('./locales/en/community'),
      import('./locales/en/ficha'),
      import('./locales/en/procedures'),
      import('./locales/en/instruments'),
      import('./locales/en/newSurgery'),
      import('./locales/en/report'),
      import('./locales/en/research'),
      import('./locales/en/profile'),
      import('./locales/en/settings'),
      import('./locales/en/surgeryDetail'),
    ]);
    return {
      account: account.default,
      community: community.default,
      ficha: ficha.default,
      procedures: procedures.default,
      instruments: instruments.default,
      newSurgery: newSurgery.default,
      report: report.default,
      research: research.default,
      profile: profile.default,
      settings: settings.default,
      surgeryDetail: surgeryDetail.default,
    };
  });
}

const initialLng = detectLanguage();
applyDocumentLanguage(initialLng);

void i18n.use(initReactI18next).init({
  resources: {},
  lng: initialLng,
  fallbackLng: 'en',
  returnEmptyString: false,
  interpolation: { escapeValue: false }, // React already escapes
  partialBundledLanguages: true,
});

const shellLoaded = new Set<AppLanguage>();
const loaded = new Set<AppLanguage>();

type EnsureOpts = {
  /**
   * When true (default), switch i18n to `lang` after loading.
   * Prefetch/warm of the other locale must pass false — otherwise idle
   * warmup flips the whole UI (often while focusing an input on iOS).
   */
  activate?: boolean;
};

/** @internal exported for unit tests */
export function shouldActivate(opts?: EnsureOpts): boolean {
  return opts?.activate !== false;
}

/** Shell strings needed before first paint (login, nav, home, onboarding). */
export async function ensureShellLanguage(
  lang: AppLanguage,
  opts?: EnsureOpts,
): Promise<void> {
  const activate = shouldActivate(opts);
  if (loaded.has(lang) || shellLoaded.has(lang)) {
    if (activate && i18n.language !== lang) await i18n.changeLanguage(lang);
    return;
  }
  const bundle = await importShell(lang);
  i18n.addResourceBundle(lang, 'translation', bundle, true, true);
  shellLoaded.add(lang);
  if (activate && i18n.language !== lang) await i18n.changeLanguage(lang);
}

/** Full locale (shell + deferred). Used at boot to finish filling strings. */
export async function ensureLanguage(
  lang: AppLanguage,
  opts?: EnsureOpts,
): Promise<void> {
  const activate = shouldActivate(opts);
  if (loaded.has(lang)) {
    if (activate && i18n.language !== lang) await i18n.changeLanguage(lang);
    return;
  }
  if (!shellLoaded.has(lang)) {
    // Never activate mid-shell when the caller asked not to flip UI.
    await ensureShellLanguage(lang, { activate: false });
  }
  const bundle = await importDeferred(lang);
  i18n.addResourceBundle(lang, 'translation', bundle, true, true);
  loaded.add(lang);
  if (activate && i18n.language !== lang) await i18n.changeLanguage(lang);
}

/** Load locale bundles without changing the active UI language. */
export async function prefetchLanguage(lang: AppLanguage): Promise<void> {
  await ensureLanguage(lang, { activate: false });
}

// Kick off shell immediately; finish the active locale + warm the other in idle.
void ensureShellLanguage(initialLng)
  .then(() => {
    void ensureLanguage(initialLng)
      .then(() => {
        const other: AppLanguage = initialLng === 'en' ? 'es' : 'en';
        const warm = () => {
          // Prefetch only — must not changeLanguage(other) or the UI flips on idle
          // (commonly while the iOS keyboard/autocomplete appears).
          void prefetchLanguage(other);
        };
        if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
          window.requestIdleCallback(warm, { timeout: 4000 });
        } else {
          globalThis.setTimeout(warm, 1500);
        }
      })
      .catch(recoverBootLocale);
  })
  .catch(recoverBootLocale);

function recoverBootLocale(err: unknown): void {
  if (isChunkLoadError(err) && tryRecoverFromStaleChunk()) return;
  console.error(err);
}

export default i18n;
